import { Link } from "@mui/material";
import { Fragment, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectDimensions } from "../../../../store/selectors/metaDataSelectors";
import { useDrillDownGridScroll } from "./DrillDownGridScrollContext.tsx";
import { getSourceColumnCaption } from "./utils/getSourceColumnCaption.ts";

interface Props {
  fields: string[];
}

/**
 * Renders a measure's source columns (`valueTargetFields`) as a `|`-separated list.
 * Each caption is a Link that scrolls the grid to that column when the field is a
 * column in the currently-open grid; otherwise it renders as plain text.
 * No Typography wrapper — the caller supplies it so the variant/colour match the title.
 */
const SourceColumnLinks = ({ fields }: Props) => {
  const dimensions = useSelector(selectDimensions);
  const { visibleColumnFields, requestScrollToField } = useDrillDownGridScroll();

  const uniqueFields = useMemo(() => [...new Set(fields)], [fields]);
  const visible = useMemo(() => new Set(visibleColumnFields), [visibleColumnFields]);

  return (
    <>
      {uniqueFields.map((field, index) => {
        const caption = getSourceColumnCaption(field, dimensions);
        return (
          <Fragment key={field}>
            {index > 0 && " | "}
            {visible.has(field) ? (
              <Link
                component="button"
                type="button"
                onClick={() => requestScrollToField(field)}
                underline="hover"
                color="primary"
                sx={{ font: "inherit", verticalAlign: "baseline" }}
              >
                {caption}
              </Link>
            ) : (
              caption
            )}
          </Fragment>
        );
      })}
    </>
  );
};

export default SourceColumnLinks;
